import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// www.squaremigration.com is the canonical host (it is also the primary domain and
// the version Search Console selected). Everything in this project (canonical tags,
// sitemap, internal links) points at www, so redirect the apex permanently.
const canonicalHostMiddleware = createMiddleware().server(async ({ next, request }) => {
  try {
    const url = new URL(request.url);
    if (url.hostname === "squaremigration.com") {
      url.hostname = "www.squaremigration.com";
      return new Response(null, { status: 301, headers: { location: url.toString() } });
    }
  } catch {
    // fall through to normal handling
  }
  return await next();
});

const RETIRED_PATH_REDIRECTS: Record<string, string> = {
  "/website-migration": "/squarespace-websites",
  "/website-refresh": "/squarespace-websites",
  "/author-sites": "/squarespace-websites",
  "/faq": "/",
  "/why-migrate": "/squarespace-websites",
  "/blog/elevate-your-website-with-square-migration-the-advantages-of-re-platforming": "/squarespace-websites",
  "/blog": "/",
  "/blog/why-assessments-beat-gated-content": "/assessments",
  "/blog/what-does-a-b2b-lead-actually-cost": "/lead-generation-tools",
  "/blog/anatomy-of-a-landing-page-that-converts": "/event-landing-pages",
};

const retiredPathRedirectMiddleware = createMiddleware().server(async ({ next, request }) => {
  try {
    const url = new URL(request.url);
    const destination = RETIRED_PATH_REDIRECTS[url.pathname];
    if (destination) {
      url.pathname = destination;
      return new Response(null, { status: 301, headers: { location: url.toString() } });
    }
  } catch {
    // fall through to normal handling
  }
  return await next();
});

// Catch-all: any unknown page URL 301s to the homepage instead of 404ing.
// Assets (paths with a file extension), API endpoints and non-HTML requests
// (crawlers probing, trackers) still get a real 404.
const catchAllNotFoundMiddleware = createMiddleware().server(async ({ next, request }) => {
  const result = await next();
  const response = (result as { response?: Response }).response;
  if (!response || response.status !== 404) return result;
  if (request.method !== "GET" && request.method !== "HEAD") return result;

  try {
    const url = new URL(request.url);
    const pathname = url.pathname;
    if (pathname.startsWith("/api/")) return result;
    if (/\.[a-zA-Z0-9]+$/.test(pathname)) return result;
    const accept = request.headers.get("accept") ?? "";
    if (!accept.includes("text/html")) return result;

    url.pathname = "/";
    return new Response(null, { status: 301, headers: { location: url.toString() } });
  } catch {
    return result;
  }
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [canonicalHostMiddleware, retiredPathRedirectMiddleware, catchAllNotFoundMiddleware, errorMiddleware],
}));
